import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeatureFlag } from '../../services/featureFlag.service';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Plus, Pencil, ArrowUpCircle, Layers } from 'lucide-react';
import { useMyTemplates } from '../../hooks/useTemplates';
import { UpgradeAllDialog } from '../../components/templates/UpgradeAllDialog';
import type { Template } from '../../services/template.service';

export default function MyTemplates() {
  const { data: templates, isLoading } = useMyTemplates();
  const [upgradeTemplate, setUpgradeTemplate] = useState<Template | null>(null);
  const isTemplateManagementEnabled = useFeatureFlag('template-management', false);

  if (!isTemplateManagementEnabled) {
    return <div className="p-6">Access denied</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/develop">Develop</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Templates</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-3">
            <Layers className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-semibold">Your Templates</h1>
              <p className="text-sm text-muted-foreground">Manage templates you've created</p>
            </div>
          </div>
          <Button asChild>
            <Link to="/templates/export">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !templates?.length ? (
        <div className="text-center py-12 text-muted-foreground">
          You haven't created any templates yet.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Version</TableHead>
              <TableHead className="w-[200px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell className="font-medium">{template.name}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{template.type}</Badge>
                </TableCell>
                <TableCell>{template.version}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/templates/edit/${template.id}`}>
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUpgradeTemplate(template)}
                    >
                      <ArrowUpCircle className="h-4 w-4 mr-1" />
                      Upgrade All
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <UpgradeAllDialog
        open={!!upgradeTemplate}
        onOpenChange={(open) => { if (!open) setUpgradeTemplate(null); }}
        template={upgradeTemplate}
      />
    </div>
  );
}
